/**
 * Логика первого входа.
 *
 * Проверка выбора выполняется здесь, а не в компоненте: то же правило нужно
 * при восстановлении настроек из хранилища и при синхронизации с сервером.
 */

import type { LanguageCode } from '@type2x/shared-contracts';
import {
  DEFAULT_LAYOUT_BY_LANGUAGE,
  KEYBOARD_FORMS,
  LANGUAGES,
  LAYOUT_IDS,
  OS_PLATFORMS,
} from '@type2x/shared-contracts';
import { getLayout } from '@type2x/keyboard-layouts';
import {
  DEFAULT_START_PATH,
  START_PATHS,
  createWorkspace,
  setKeyboard,
  setStartPath,
  switchLanguage,
  type StartPath,
  type Workspace,
  type WorkspaceOwner,
} from '@type2x/feature-personal-workspace';

import type { ChoiceOption, ChoiceValidation, OnboardingChoice } from '../contracts/choices.js';

/** Варианты языка обучения. */
export function languageOptions(): readonly ChoiceOption<LanguageCode>[] {
  return LANGUAGES.map((value) => ({ value }));
}

/** Варианты операционной системы. */
export function platformOptions(): readonly ChoiceOption<(typeof OS_PLATFORMS)[number]>[] {
  return OS_PLATFORMS.map((value) => ({ value }));
}

/** Варианты формы клавиатуры. */
export function formOptions(): readonly ChoiceOption<(typeof KEYBOARD_FORMS)[number]>[] {
  return KEYBOARD_FORMS.map((value) => ({ value }));
}

/** Варианты начала обучения. */
export function startOptions(): readonly ChoiceOption<StartPath>[] {
  return START_PATHS.map((value) => ({ value }));
}

/** Раскладки, доступные для языка. */
export function layoutOptionsFor(
  language: LanguageCode,
): readonly ChoiceOption<(typeof LAYOUT_IDS)[number]>[] {
  return LAYOUT_IDS.filter((id) => getLayout(id).language === language).map((value) => ({ value }));
}

/** Выбор по умолчанию для языка интерфейса. */
export function defaultChoice(language: LanguageCode): OnboardingChoice {
  return {
    language,
    layoutId: DEFAULT_LAYOUT_BY_LANGUAGE[language],
    platform: 'windows',
    form: 'ansi',
    start: DEFAULT_START_PATH,
  };
}

/**
 * Проверяет согласованность выбора.
 *
 * Раскладка обязана соответствовать языку: русский текст в QWERTY не
 * набирается, и молча подменять выбор нельзя — пользователь не поймёт, что
 * произошло.
 */
export function validateChoice(choice: OnboardingChoice): ChoiceValidation {
  if (!(LAYOUT_IDS as readonly string[]).includes(choice.layoutId)) {
    return { ok: false, problem: 'unknown-layout' };
  }
  if (getLayout(choice.layoutId).language !== choice.language) {
    return { ok: false, problem: 'layout-language-mismatch' };
  }
  return {
    ok: true,
    keyboard: { layoutId: choice.layoutId, platform: choice.platform, form: choice.form },
  };
}

/**
 * Добавляет в пространство профиль языка и делает его активным.
 *
 * Тот же выбор, что и при первом входе, но язык интерфейса не трогается: он
 * задаётся один раз и к языку тренировки отношения не имеет. Профиль второго
 * языка ничем не отличается от первого — своя раскладка, свой прогресс, своё
 * «с чего начать».
 *
 * @throws {Error} если выбор несогласован: несогласованный профиль привёл бы
 *   к неверной статистике по клавишам.
 */
export function addLanguage(
  workspace: Workspace,
  choice: OnboardingChoice,
  now: string,
): Workspace {
  const validation = validateChoice(choice);
  if (!validation.ok) {
    throw new Error(`Несогласованный выбор языка: ${validation.problem}`);
  }

  const withKeyboard = setKeyboard(
    switchLanguage(workspace, choice.language, now),
    validation.keyboard,
    now,
  );
  return setStartPath(withKeyboard, choice.start, now);
}

/**
 * Создаёт гостевое пространство по выбору пользователя.
 *
 * @throws {Error} если выбор несогласован: несогласованное пространство
 *   привело бы к неверной статистике по клавишам.
 */
export function initialiseWorkspace(
  owner: WorkspaceOwner,
  choice: OnboardingChoice,
  uiLanguage: LanguageCode,
  now: string,
): Workspace {
  const validation = validateChoice(choice);
  if (!validation.ok) {
    throw new Error(`Несогласованный выбор при первом входе: ${validation.problem}`);
  }

  return addLanguage(createWorkspace(owner, uiLanguage, now), choice, now);
}
